import * as React from 'react';
import { useState } from 'react';
import {
  Button,
  Divider,
  Field,
  Input,
  Link,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  Text,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import {
  Settings20Regular,
  WeatherMoon20Regular,
  WeatherSunny20Regular,
} from '@fluentui/react-icons';
import {
  AuthUser,
  clearStoredUser,
  loginWithCredentials,
  loginWithGoogle,
  MfaDetail,
  MfaMethod,
  MfaRequiredError,
  sendMfaCode,
} from '../../services/auth';
import {
  GoogleAuthCancelledError,
  isGoogleAuthAvailable,
  requestGoogleIdToken,
} from '../../services/googleAuth';
import { GOOGLE_CLIENT_ID, SIGNUS_APP_URL } from '../../services/constants';
import { FontSize, useAppearance } from '../appearance';

/* global HTMLInputElement */

interface SettingsPanelProps {
  user: AuthUser | null;
  onAuthChange: (user: AuthUser | null) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Google's four-colour "G". Inline rather than an asset so it can't be blocked as a remote
 * image, and so it stays legible on both themes without a light/dark variant. */
const GoogleMark: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path
      fill="#4285F4"
      d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
    />
    <path
      fill="#34A853"
      d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
    />
    <path
      fill="#FBBC05"
      d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
    />
    <path
      fill="#EA4335"
      d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
    />
  </svg>
);

const useStyles = makeStyles({
  trigger: {
    minWidth: '32px',
    width: '32px',
    height: '32px',
    padding: 0,
  },
  surface: {
    width: '300px',
    maxWidth: '92vw',
    padding: '14px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sectionLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  signinBtn: {
    width: '100%',
  },
  forgot: {
    alignSelf: 'flex-start',
    fontSize: tokens.fontSizeBase200,
  },
  orRow: {
    marginTop: '4px',
  },
  orDivider: {
    fontSize: tokens.fontSizeBase200,
  },
  orText: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
  },
  divider: {
    margin: '14px 0',
  },
  appearanceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  fontSizeBtns: {
    display: 'flex',
    gap: '4px',
  },
  fontBtn: {
    minWidth: '32px',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  loggedInRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  loggedInInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  loggedInEmail: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '180px',
  },
  mfaHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  mfaMethodBtns: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  mfaActionsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
});

const FONT_LABEL: Record<FontSize, string> = {
  small: 'S',
  medium: 'M',
  large: 'L',
};

const MFA_LABEL: Record<MfaMethod, string> = {
  EMAIL: 'Email',
  VIRTUAL: 'Authenticator app',
  SMS: 'Text message',
};

// Mirrors the default method the backend auto-sends a code for when none is specified.
const MFA_PREFERRED_ORDER: MfaMethod[] = ['EMAIL', 'VIRTUAL', 'SMS'];

function pickDefaultMfaMethod(methods: MfaDetail[]): MfaDetail {
  return (
    MFA_PREFERRED_ORDER.map(type => methods.find(m => m.type === type)).find(Boolean) ?? methods[0]
  );
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  user,
  onAuthChange,
  open,
  onOpenChange,
}) => {
  const styles = useStyles();
  const { fontSize, isDark, setFontSize, toggleDark } = useAppearance();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [googleLoading, setGoogleLoading] = useState(false);

  const [mfaMethods, setMfaMethods] = useState<MfaDetail[] | null>(null);
  const [selectedMfaMethod, setSelectedMfaMethod] = useState<MfaDetail | null>(null);
  const [otp, setOtp] = useState('');
  const [resending, setResending] = useState(false);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setMfaMethods(null);
    setSelectedMfaMethod(null);
    setOtp('');
  };

  const handleSignIn = async () => {
    if (!email.trim() || !password) return;
    setError(null);
    setLoading(true);
    try {
      const loggedInUser = await loginWithCredentials(email.trim(), password);
      resetForm();
      onAuthChange(loggedInUser);
    } catch (e) {
      if (e instanceof MfaRequiredError) {
        setMfaMethods(e.methods);
        setSelectedMfaMethod(pickDefaultMfaMethod(e.methods));
      } else {
        setError(e instanceof Error ? e.message : 'Sign-in failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      const idToken = await requestGoogleIdToken();
      const loggedInUser = await loginWithGoogle(idToken);
      resetForm();
      onAuthChange(loggedInUser);
    } catch (e) {
      // Closing the dialog is a deliberate "never mind", not an error worth surfacing.
      if (!(e instanceof GoogleAuthCancelledError)) {
        setError(e instanceof Error ? e.message : 'Google sign-in failed.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!otp.trim() || !selectedMfaMethod) return;
    setError(null);
    setLoading(true);
    try {
      const loggedInUser = await loginWithCredentials(
        email.trim(),
        password,
        otp.trim(),
        selectedMfaMethod.type,
      );
      resetForm();
      onAuthChange(loggedInUser);
    } catch (e) {
      if (e instanceof MfaRequiredError) {
        setMfaMethods(e.methods);
      }
      setOtp('');
      setError(e instanceof Error ? e.message : 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaMethods(null);
    setSelectedMfaMethod(null);
    setOtp('');
    setError(null);
  };

  const handleResendCode = async () => {
    if (
      !selectedMfaMethod ||
      (selectedMfaMethod.type !== 'EMAIL' && selectedMfaMethod.type !== 'SMS')
    ) {
      return;
    }
    setResending(true);
    setError(null);
    try {
      await sendMfaCode(selectedMfaMethod.type, selectedMfaMethod.id, email.trim(), password);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not resend code.');
    } finally {
      setResending(false);
    }
  };

  const handleLogout = () => {
    clearStoredUser();
    onAuthChange(null);
  };

  return (
    <Popover
      positioning="below-start"
      trapFocus
      open={open}
      onOpenChange={(_, data) => onOpenChange(data.open)}
    >
      <PopoverTrigger disableButtonEnhancement>
        <Button
          className={styles.trigger}
          appearance="subtle"
          icon={<Settings20Regular />}
          aria-label="Settings"
        />
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        {/* ---- Signus account ---- */}
        <div className={styles.section}>
          <Text className={styles.sectionLabel}>Signus account</Text>
          {user ? (
            <div className={styles.loggedInRow}>
              <div className={styles.loggedInInfo}>
                <Text weight="semibold">
                  {user.firstName}
                  {user.lastName ? ` ${user.lastName}` : ''}
                </Text>
                <span className={styles.loggedInEmail}>{user.email}</span>
              </div>
              <Button size="small" appearance="subtle" onClick={handleLogout}>
                Sign out
              </Button>
            </div>
          ) : mfaMethods && selectedMfaMethod ? (
            <>
              {mfaMethods.length > 1 && (
                <div className={styles.mfaMethodBtns}>
                  {mfaMethods.map(m => (
                    <Button
                      key={m.type}
                      size="small"
                      appearance={selectedMfaMethod.type === m.type ? 'primary' : 'secondary'}
                      onClick={() => setSelectedMfaMethod(m)}
                    >
                      {MFA_LABEL[m.type]}
                    </Button>
                  ))}
                </div>
              )}
              <Field
                label={`Verification code${selectedMfaMethod.type !== 'VIRTUAL' ? ` (sent via ${MFA_LABEL[selectedMfaMethod.type].toLowerCase()})` : ''}`}
              >
                <Input
                  type="text"
                  autoFocus
                  value={otp}
                  placeholder="123456"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOtp(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') handleVerifyCode();
                  }}
                />
              </Field>
              {selectedMfaMethod.hint && (
                <Text className={styles.mfaHint}>Sent to {selectedMfaMethod.hint}</Text>
              )}
              <Button
                className={styles.signinBtn}
                appearance="primary"
                disabled={!otp.trim() || loading}
                icon={loading ? <Spinner size="tiny" /> : undefined}
                onClick={handleVerifyCode}
              >
                Verify
              </Button>
              <div className={styles.mfaActionsRow}>
                <Link className={styles.forgot} onClick={handleBackToLogin}>
                  Back
                </Link>
                {(selectedMfaMethod.type === 'EMAIL' || selectedMfaMethod.type === 'SMS') && (
                  <Link className={styles.forgot} onClick={handleResendCode} disabled={resending}>
                    {resending ? 'Sending…' : 'Resend code'}
                  </Link>
                )}
              </div>
              {error && (
                <MessageBar intent="error">
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              )}
            </>
          ) : (
            <>
              <Field label="Email">
                <Input
                  type="email"
                  value={email}
                  placeholder="you@example.com"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                />
              </Field>
              <Field label="Password">
                <Input
                  type="password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') handleSignIn();
                  }}
                />
              </Field>
              <Button
                className={styles.signinBtn}
                appearance="primary"
                disabled={!email.trim() || !password || loading}
                icon={loading ? <Spinner size="tiny" /> : undefined}
                onClick={handleSignIn}
              >
                Sign in
              </Button>
              <Link
                className={styles.forgot}
                href={`${SIGNUS_APP_URL}/reset-password`}
                target="_blank"
              >
                Forgot password?
              </Link>
              {GOOGLE_CLIENT_ID && isGoogleAuthAvailable() && (
                <>
                  <div className={styles.orRow}>
                    <Divider className={styles.orDivider}>
                      <span className={styles.orText}>Or continue with…</span>
                    </Divider>
                  </div>
                  <Button
                    className={styles.signinBtn}
                    disabled={googleLoading || loading}
                    icon={googleLoading ? <Spinner size="tiny" /> : <GoogleMark />}
                    onClick={handleGoogleSignIn}
                  >
                    Google
                  </Button>
                </>
              )}
              {error && (
                <MessageBar intent="error">
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              )}
            </>
          )}
        </div>

        <Divider className={styles.divider} />

        {/* ---- Appearance ---- */}
        <div className={styles.section}>
          <Text className={styles.sectionLabel}>Appearance</Text>
          <div className={styles.appearanceRow}>
            <Text size={200}>Font size</Text>
            <div className={styles.fontSizeBtns}>
              {(['small', 'medium', 'large'] as FontSize[]).map(s => (
                <Button
                  key={s}
                  className={styles.fontBtn}
                  size="small"
                  appearance={fontSize === s ? 'primary' : 'secondary'}
                  onClick={() => setFontSize(s)}
                >
                  {FONT_LABEL[s]}
                </Button>
              ))}
            </div>
          </div>
          <div className={styles.appearanceRow}>
            <Text size={200}>Theme</Text>
            <Tooltip
              content={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              relationship="label"
            >
              <Button
                size="small"
                appearance="secondary"
                icon={isDark ? <WeatherSunny20Regular /> : <WeatherMoon20Regular />}
                onClick={toggleDark}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              />
            </Tooltip>
          </div>
        </div>
      </PopoverSurface>
    </Popover>
  );
};

export default SettingsPanel;
